import React, { useCallback } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { When } from 'react-if'
import { InternalLink } from '@/atoms/InternalLink'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import BlogHeroImage from '@/organisms/BlogHeroImage'
import { SocialSharing } from '@/organisms/SocialSharing'
import { extractCloudinaryImage, extractMobileCloudinaryImage } from '@/services/BlogService'
import type { BlogPost } from '@/services/BlogService'
import { renderOptions } from '@/services/ContentfulService'
import { useGuildMember } from '@/services/GuildUserService'
import { ProjectDetails } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { PayItForwardSection } from '@/views/BlogPostView/PayItForwardSection'
import { AppPromo } from './AppPromo'
import { JoinTheGuildSection } from './JoinTheGuildSection'
import { RecentPosts } from './RecentPosts'

interface PostViewProps {
  recentPosts: BlogPost[]
  post: BlogPost
  preview: boolean
  project: ProjectDetails
}

const PostView: React.FC<PostViewProps> = ({ recentPosts, post, preview, project }) => {
  const cloudinaryImg = extractCloudinaryImage(post)
  const cloudinaryMobileImg = extractMobileCloudinaryImage(post)
  const hidePayItForward = Boolean(post?.hidePayItForward)
  const track = useSafeTrack()
  const { isGuildMember } = useGuildMember()

  const onPifClick = useCallback(() => {
    track('Blog Clicked Pay It Forward')
  }, [track])

  const onGuildClick = useCallback(() => {
    track('Blog Clicked Join The Guild')
  }, [track])

  return (
    <div className="relative bg-white">
      <div className="absolute top-0 z-10 h-12 w-full bg-gradient-to-b from-core-gray-950 to-transparent sm:h-24 lg:h-32" />
      <BlogHeroImage
        alt={post?.header}
        cloudinaryImgPath={`v${cloudinaryImg?.version}/${cloudinaryImg?.public_id}.webp`}
        cloudinaryMobileImgPath={
          cloudinaryMobileImg ? `v${cloudinaryMobileImg?.version}/${cloudinaryMobileImg?.public_id}.webp` : undefined
        }
        cloudinaryImageDesktopTransformation={post.cloudinaryImageDesktopTransformation}
        cloudinaryImageMobileTransformation={post.cloudinaryImageMobileTransformation}
        desktopImageClassName="lg:max-h-[36rem]"
        mobileImageClassName="min-h-[16rem]"
      />
      <LazyHydrate whenVisible>
        <PaddedContainer>
          <div className="grid grid-cols-1 gap-6 pb-20 pt-4 lg:grid-cols-6">
            <main className="col-span-1 md:col-span-4">
              <InternalLink href={paths.blog.index}>{`< Back`}</InternalLink>
              <h1 className="pt-4 text-[2rem] font-black leading-10 md:text-5xl">{post?.header}</h1>
              <div className="pt-4 md:pt-8">
                by Angel |{' '}
                {new Date(post?.sys?.firstPublishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <BeforeYouLeaveProvider>
                <BeforeYouLeaveModal />
                {documentToReactComponents(post.content.json, renderOptions({ links: post.content.links, preview }))}
              </BeforeYouLeaveProvider>
              <div className="h-fit w-fit py-8">
                <When condition={isGuildMember && !hidePayItForward}>
                  <PayItForwardSection project={project} onClick={onPifClick} />
                </When>
                <When condition={!isGuildMember}>
                  <JoinTheGuildSection project={project} onClick={onGuildClick} />
                </When>
              </div>
              <div className="pb-10 pt-9">
                <SocialSharing
                  shareUrl={encodeURI(`${paths.getBaseURL()}${paths.blog.index}/${project.slug}/posts/${post?.slug}`)}
                />
              </div>
            </main>
            <div className="col-span-1 flex flex-col gap-16 md:col-span-2">
              <RecentPosts posts={recentPosts} project={project} />
              <AppPromo />
            </div>
          </div>
        </PaddedContainer>
      </LazyHydrate>
    </div>
  )
}

export default PostView
